// Matches a raw EVU file's stream_sources against the deployment's available cameras and
// rebuilds the UI-only state (allStreamSources / selectedIndices / audioLinks / recordingIndices)
// that the form sections use to render accordions and checkboxes.
//
// This is the single source of truth for turning "a stream_sources array" into form UI state.
// It must be used both when a template is first loaded AND whenever stream_sources is replaced
// wholesale (e.g. from the JSON editor or a file upload) — otherwise allStreamSources/
// selectedStreamSources go out of sync with values.file.stream_sources and accordions render
// duplicated/shifted entries.
export const deriveStreamSourceState = (parsedFile, deploymentStreamSources) => {
  const selectedIndices = [];
  const matchedFormSources = [];
  const matchedCamIds = new Map();
  const audioSourcesToAdd = [];
  const audioLinks = new Map();

  if (parsedFile.stream_sources && Array.isArray(parsedFile.stream_sources)) {
    parsedFile.stream_sources.forEach(templateSrc => {
      if (matchedCamIds.has(templateSrc.cam_id)) {
        const hasAudioParam = templateSrc.url?.includes("audio=1");
        const isAudioLabel = templateSrc.label === "AUDIO";

        if (hasAudioParam || isAudioLabel) {
          audioSourcesToAdd.push({
            templateSrc,
            parentCamId: templateSrc.cam_id,
          });
          return;
        }
        // Repeated cam_id that isn't an audio companion: additional virtual cameras
        // (vptz) share the base camera's cam_id, so treat it as its own source instead
        // of discarding it.
      }

      const deploymentIndex = deploymentStreamSources.findIndex(depSrc => depSrc.cam_id === templateSrc.cam_id);

      if (deploymentIndex !== -1) {
        selectedIndices.push(deploymentIndex);
        matchedFormSources.push(templateSrc);
        matchedCamIds.set(templateSrc.cam_id, templateSrc);
      } else {
        selectedIndices.push(-1);
        matchedFormSources.push(templateSrc);
        matchedCamIds.set(templateSrc.cam_id, templateSrc);
      }
    });
  }

  let updatedAllStreamSources = [...deploymentStreamSources];
  let updatedSelectedIndices = [...selectedIndices];
  let updatedFormSources = matchedFormSources.map(src => {
    const hasExposureData =
      (!!src?.exp_camera_url && String(src.exp_camera_url).trim() !== "") ||
      (!!src?.exp_camera_cfg && String(src.exp_camera_cfg).trim() !== "");
    return hasExposureData ? { ...src, useAutoExposure: true } : src;
  });

  for (let i = 0; i < updatedSelectedIndices.length; i++) {
    if (updatedSelectedIndices[i] === -1) {
      const templateSrc = updatedFormSources[i];

      const newSource = {
        cam_id: templateSrc.cam_id || `template_${i}`,
        description: templateSrc.description || templateSrc.label || "Template Source",
        brand: templateSrc.brand || "Unknown",
        url: templateSrc.url || "",
        label: templateSrc.label,
      };

      const newIndex = updatedAllStreamSources.length;
      updatedAllStreamSources.push(newSource);

      updatedSelectedIndices[i] = newIndex;
    }
  }

  audioSourcesToAdd.forEach(({ templateSrc, parentCamId }) => {
    const parentDeploymentIndex = updatedAllStreamSources.findIndex(src => src.cam_id === parentCamId);

    if (parentDeploymentIndex !== -1) {
      const parentDeploymentSource = updatedAllStreamSources[parentDeploymentIndex];

      const audioSource = {
        description: `${parentDeploymentSource.description || ""} (Audio)`,
        cam_id: parentDeploymentSource.cam_id,
        brand: parentDeploymentSource.brand,
        url: templateSrc.url,
        label: templateSrc.label || "AUDIO",
        isAudioSource: true,
        parentIndex: parentDeploymentIndex,
      };

      const audioIndex = parentDeploymentIndex + 1;
      updatedAllStreamSources.splice(audioIndex, 0, audioSource);

      updatedSelectedIndices = updatedSelectedIndices.map(idx => (idx >= audioIndex ? idx + 1 : idx));
      updatedSelectedIndices.push(audioIndex);

      updatedFormSources.push(templateSrc);

      const newParentIndex = parentDeploymentIndex >= audioIndex ? parentDeploymentIndex + 1 : parentDeploymentIndex;
      audioLinks.set(newParentIndex, audioIndex);
    }
  });

  const matchedLabels = updatedFormSources.map(src => src.label).filter(Boolean);
  const filteredFile = {
    type: "EVU",
    version: "2.00",
    ...parsedFile,
    stream_sources: updatedFormSources,
  };

  if (filteredFile.mixer) {
    filteredFile.mixer = {
      ...filteredFile.mixer,
      video: (filteredFile.mixer.video || []).filter(label => matchedLabels.includes(label)),
      audio: (filteredFile.mixer.audio || []).filter(label => matchedLabels.includes(label)),
    };

    // If the saved template already has both drawn and burnt scoreboards,
    // keep the secondary one visible in the form (UI-only flag).
    if (filteredFile.mixer.score && filteredFile.mixer.score.drawn && filteredFile.mixer.score.burnt) {
      filteredFile.mixer.score = {
        ...filteredFile.mixer.score,
        include_secondary: true,
      };
    }

    // If the template has no scoreboard at all, make that explicit so the UI shows
    // "none" and buildFilePayload removes the score block instead of sending an
    // empty/incomplete scoreboard that the EVU tester would reject.
    if (!filteredFile.mixer.score) {
      filteredFile.mixer.score = { default: "none" };
    }
  }

  if (filteredFile.video_drains?.recordings) {
    filteredFile.video_drains.recordings = filteredFile.video_drains.recordings.filter(
      rec => rec.src === "program" || matchedLabels.includes(rec.src),
    );
  }

  let recordingIndices = [];
  if (filteredFile.video_drains?.recordings && Array.isArray(filteredFile.video_drains.recordings)) {
    const recordingLabels = filteredFile.video_drains.recordings
      .filter(rec => rec.src !== "program")
      .map(rec => rec.src);

    updatedFormSources.forEach((formSrc, formIndex) => {
      if (recordingLabels.includes(formSrc.label)) {
        const sourceIndex = updatedSelectedIndices[formIndex];
        if (sourceIndex !== undefined && !recordingIndices.includes(sourceIndex)) {
          recordingIndices.push(sourceIndex);
        }
      }
    });
  }

  return {
    filteredFile,
    allStreamSources: updatedAllStreamSources,
    selectedIndices: updatedSelectedIndices,
    audioLinks,
    recordingIndices,
  };
};
